import { inject, Injectable, Injector } from '@angular/core';
import {
    CompanyController,
    DocumentController,
    DocumentEntity,
    DocumentItemConfigsPartial,
    DocumentItemPartial,
    DocumentItemTypeEnum,
} from '@domain';
import { stripDeltaAttribute } from '../../../delta-attributes.util';
import { TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import {
    catchError,
    concat,
    defer,
    EMPTY,
    filter,
    from,
    ignoreElements,
    map,
    mergeMap,
    Observable,
    of,
    retry,
    shareReplay,
    switchMap,
    tap,
    throwError,
    timer,
    toArray,
} from 'rxjs';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { CreateFieldResponseDTO } from 'src/app/domain/DTOs/create-field.resp.dto';
import { MediaType, UploadProgressService } from 'src/app/shared/services/upload-progress.service';
import { DigitalOceanStorage } from 'src/app/shared/utils/digital-ocean/digital-ocean-storage';
import { MediaUtils } from 'src/app/shared/utils/media.utils';
import { DEFAULT_CALLOUT_COLOR } from '../../../callout-colors';
import { ChartValue } from '../../../chart-render/chart-render';
import {
    DocumentSemaphoreValue,
    initialSemaphoreValue,
    SEMAPHORE_SCALE_LABEL_KEYS,
} from '../../../semaphore-options';
import {
    DEFAULT_TABLE_STYLE,
    DEFAULT_TABLE_ZEBRA,
    DocumentTableValue,
    initialTableValue,
} from '../../../table-options';
import {
    DEFAULT_STATS_COLUMNS,
    DEFAULT_STATS_STYLE,
    DocumentStatEntry,
    initialStatsEntries,
} from '../../../stats-options';
import { EditDataService } from './_edit-data.service';
import { EditService } from './edit-service';
import { FieldRemovalService } from './field-removal.service';
import { ImageColumnsService } from './image-columns.service';
import { OriginalTextService } from './original-text.service';

@Injectable({
    providedIn: 'root',
})
export class EditItemsService {
    /** Uploads simultâneos por lote (o browser já limita conexões por host). */
    private static readonly UPLOAD_CONCURRENCY = 4;

    /**
     * Teto de imagens em UM campo de grupo — o mesmo do servidor
     * (`DOCUMENTS_CONFIGS.MAX_IMAGES_PER_GROUP_FIELD`). Seleção maior que isso vira
     * mais de um grupo, em vez de ser recusada depois do upload inteiro.
     */
    private static readonly MAX_IMAGES_PER_GROUP = 50;

    /** Último save de config em voo por campo — encadeia os seguintes (ver `_enqueueItemConfigSave`). */
    private _itemConfigSaveQueue = new Map<string, Observable<unknown>>();

    private _injector = inject(Injector);

    // * [Injects]
    private _mediaUtils = inject(MediaUtils);
    private _editService = inject(EditService);
    private _editDataService = inject(EditDataService);
    private _documentController = inject(DocumentController);
    private _documentFieldsController = inject(DocumentFieldsController);
    private _digitalOceanStorage = inject(DigitalOceanStorage);
    private _uploadProgressService = inject(UploadProgressService);
    private _companyController = inject(CompanyController);
    private _toastService = inject(RaToastService);
    private _fieldRemovalService = inject(FieldRemovalService);
    private _originalTextService = inject(OriginalTextService);
    private _imageColumnsService = inject(ImageColumnsService);

    private _t(key: string, params?: Record<string, string | number>): string {
        return this._injector.get(TranslateService).instant(key, params);
    }

    private _imagesBatchLabel(imagesLength: number): string {
        return imagesLength === 1
            ? this._t('document.edit.service.imagesBatchOne')
            : this._t('document.edit.service.imagesBatchMany', { count: imagesLength });
    }

    /**
     * Cria o card de progresso do lote ANTES de comprimir e reporta o avanço nele.
     *
     * A compressão é a etapa mais longa quando o usuário seleciona muitas imagens
     * (~100): sem o card, a tela fica parada logo depois do seletor de arquivos
     * fechar e parece travada.
     */
    private _compressImagesWithBatchProgress(
        images: File[],
        batchUploadId: string,
        mediaType: MediaType
    ): Observable<File[]> {
        const totalBytes = images.reduce((acc, file) => acc + file.size, 0);

        this._uploadProgressService.addCompressing(
            batchUploadId,
            this._imagesBatchLabel(images.length),
            totalBytes,
            mediaType
        );

        return this._mediaUtils
            .compressImages(images, 1920, 1920, 0.8, {
                concurrency: 2,
                onBatchProgress: progress =>
                    this._uploadProgressService.updateCompressionProgress(batchUploadId, progress),
                onSkipped: (skipped, total) => this._notifyPartialCompressFailure(skipped, total),
            })
            .pipe(tap(() => this._uploadProgressService.startUploading(batchUploadId)));
    }

    /**
     * Encerra o card do lote com erro visível. Sem isso o card some e o usuário
     * fica sem nenhum retorno de que o envio falhou.
     */
    private _failBatchCard(batchUploadId: string | undefined, alreadyShown: boolean): void {
        if (!batchUploadId || alreadyShown) return;

        this._uploadProgressService.errorUpload(
            batchUploadId,
            this._t('document.edit.service.mediaProcessError')
        );
    }

    /** Fecha o card do lote: a mídia já está gravada no documento. */
    private _finishBatchCard(batchUploadId: string | undefined): void {
        if (batchUploadId) this._uploadProgressService.completeUpload(batchUploadId);
    }

    /**
     * Solta o loading do editor assim que o card de progresso do envio existe.
     *
     * O menu "Inserir campo" liga o `loading` antes de chamar addImages/addVideo e só
     * desliga no fim do fluxo. Com isso, durante minutos de compressão e envio, inserir
     * campos e as ações do cabeçalho ficavam travados; e o primeiro save de texto no meio
     * do envio desligava tudo mesmo assim. O card do envio já mostra o progresso, então o
     * editor volta a responder a partir daqui. O desligar do menu no fim continua valendo
     * (é o mesmo valor).
     */
    private _releaseEditorLoading(): void {
        this._editService.loading.set(false);
    }

    /**
     * Barra o lote que não cabe no crédito do plano, ANTES de subir os arquivos.
     *
     * Entra depois da compressão de propósito: é ali que o tamanho final (o mesmo que
     * o backend vai cobrar) passa a ser conhecido. Checar com o tamanho original
     * recusaria lotes que na verdade caberiam — a compressão costuma cortar 90%.
     *
     * O backend responde 402 e o controller já exibe a mensagem; aqui só propagamos o
     * erro para o `catchError` do fluxo encerrar o card do lote.
     */
    private _assertCanAddMedia<T extends { size: number }>(
        documentId: string,
        items: T[],
        mediaType: 'image' | 'video'
    ): Observable<T[]> {
        const totalSizeToAdd = items.reduce((total, item) => total + (item.size || 0), 0);

        return this._documentController
            .checkCanAddMediaToDocument(documentId, mediaType, items.length, totalSizeToAdd)
            .pipe(map(() => items));
    }

    /**
     *
     * @param aboveFieldId ID of field above
     * @param belowFieldId ID of field below
     * @returns
     */
    public addText(
        aboveFieldId?: string,
        belowFieldId?: string,
        subtitle?: boolean,
        value?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = subtitle ? DocumentItemTypeEnum.SUBTITLE : DocumentItemTypeEnum.TEXT;
        if (value) {
            newItem.value = value;
        }

        // Captura a pasta no início da ação (ver _createItem).
        const folderId = this._editDataService.currentFolderId();

        // Cria o item
        return this._createItem([newItem], aboveFieldId, belowFieldId, folderId);
    }

    /**
     * Adiciona imagens.
     *
     * Caminho ÚNICO de imagem do menu — o que decide o formato do campo é quantas
     * imagens o usuário escolhe no seletor, não uma opção separada:
     *
     * - 1 imagem → um card de imagem comum, sem pergunta nenhuma.
     * - várias  → perguntamos em quantas colunas elas entram. "1 coluna" cria um
     *   card por imagem (o antigo "Imagens"); 2 ou 3 criam UM card de grupo com
     *   esse grid (o antigo "Grupo de imagens").
     *
     * A pergunta vem ANTES de comprimir e subir: cancelar não custa upload nenhum,
     * e a resposta ainda define o rótulo do card de progresso do lote.
     */
    public addImages(
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        // Pasta capturada no início (antes do upload assíncrono) — ver _createItem.
        const folderId = this._editDataService.currentFolderId();
        let batchUploadId: string | undefined;
        /** Evita remover o card quando `_uploadFiles` já chamou `errorUpload` (mensagem + timeout). */
        let batchUploadErrorShown = false;

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) {
            return throwError(() => new Error(this._t('document.edit.service.documentNotFound')));
        }

        // Seleciona imagens sem comprimir primeiro
        return this._mediaUtils
            .selectImagesFromGalleryWithoutCompress(this._companyController.getMaxImagesPerUpload())
            .pipe(
                // Filtra imagens
                filter(images => images.length > 0),

                // Com mais de uma imagem, o usuário escolhe o número de colunas.
                // Cancelar completa o fluxo sem emitir: nada é enviado. Com uma
                // imagem só não há pergunta, e `null` marca isso: o card nasce
                // herdando a config do documento, como sempre nasceu.
                switchMap(images =>
                    images.length === 1
                        ? of({ images, columns: null as number | null })
                        : this._imageColumnsService
                              .ask(images.length)
                              .pipe(map(columns => ({ images, columns })))
                ),

                // Comprime imagens (card + progresso durante compressão) e monta os
                // items para solicitar as URLs de upload
                switchMap(({ images, columns }) => {
                    const isGroup = (columns ?? 1) > 1;
                    batchUploadId = this._generateUploadId(
                        isGroup ? 'group-img-batch' : 'img-batch',
                        0
                    );
                    // O card do lote nasce na compressão, logo abaixo.
                    this._releaseEditorLoading();

                    return this._compressImagesWithBatchProgress(
                        images,
                        batchUploadId,
                        isGroup ? 'group-image' : 'image'
                    ).pipe(
                        map(compressed => ({
                            columns,
                            items: compressed.map((file, index) => ({
                                index,
                                file,
                                mimeType: file.type,
                                size: this._mediaUtils.getFileSizeInGB(file),
                                url: '',
                            })),
                        }))
                    );
                }),

                // Barra o lote que não cabe no crédito (antes de gastar banda com o
                // upload) e gera as URLs de upload
                switchMap(({ items, columns }) =>
                    this._assertCanAddMedia(documentId, items, 'image').pipe(
                        switchMap(() =>
                            this._documentController.generateUrlsToUploads(documentId, items)
                        ),
                        map(urls => ({ items, urls, columns }))
                    )
                ),

                // Realiza o upload das imagens
                switchMap(({ items, urls, columns }) =>
                    this._uploadFiles(items, urls, {
                        aggregateProgressId: batchUploadId!,
                        onAggregateUploadError: () => {
                            batchUploadErrorShown = true;
                        },
                        // Mantém apenas os arquivos que subiram com sucesso
                    }).pipe(map(() => ({ items: items.filter(item => !!item.url), columns })))
                ),

                // Cria os items no documento
                switchMap(({ items, columns }) =>
                    this._createItem(
                        (columns ?? 1) > 1
                            ? this._buildGroupImageItems(items, columns!)
                            : this._buildSingleImageItems(items, columns),
                        aboveFieldId,
                        belowFieldId,
                        folderId
                    )
                ),

                // Gravado no documento: só agora o card do lote pode sumir.
                tap(() => this._finishBatchCard(batchUploadId)),

                catchError(error => {
                    this._failBatchCard(batchUploadId, batchUploadErrorShown);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Um campo IMAGE por arquivo (escolha de "1 coluna", ou imagem única).
     *
     * `columns` nulo é a imagem única, que não passa pela pergunta: o card herda
     * a config do documento como sempre herdou.
     */
    private _buildSingleImageItems(
        items: { url?: string; size: number }[],
        columns: number | null
    ): DocumentItemPartial[] {
        const configs =
            columns === null ? undefined : this._gridOverride('singleImageGrid', columns);

        return items.map(item => ({
            type: DocumentItemTypeEnum.IMAGE,
            value: item.url,
            size: item.size,
            ...(configs ? { configs } : {}),
        }));
    }

    /**
     * Campos GROUP_IMAGE com o grid escolhido (2 ou 3 colunas).
     *
     * O lote é quebrado no teto de imagens por grupo do servidor, em múltiplos do
     * número de colunas para nenhum grupo terminar com uma linha pela metade no
     * meio do documento.
     */
    private _buildGroupImageItems(
        items: { url?: string; size: number }[],
        columns: number
    ): DocumentItemPartial[] {
        const configs = this._gridOverride('groupImageGrid', columns);
        const chunkSize =
            Math.floor(EditItemsService.MAX_IMAGES_PER_GROUP / columns) * columns || columns;

        const groups: DocumentItemPartial[] = [];
        for (let start = 0; start < items.length; start += chunkSize) {
            const chunk = items.slice(start, start + chunkSize);

            groups.push({
                type: DocumentItemTypeEnum.GROUP_IMAGE,
                value: chunk.map(item => ({ url: item.url!, size: item.size })),
                size: chunk.reduce((acc, item) => acc + item.size, 0),
                ...(configs ? { configs } : {}),
            });
        }

        return groups;
    }

    /**
     * Override de grid do campo, ou `undefined` quando o valor escolhido é o mesmo
     * que ele herdaria da config do documento.
     *
     * Gravar o valor herdado congelaria o card numa config que o usuário não
     * pediu: mudar depois a config do documento não teria efeito nessas imagens.
     */
    private _gridOverride(
        key: 'singleImageGrid' | 'groupImageGrid',
        columns: number
    ): DocumentItemConfigsPartial | undefined {
        const inherited = this._editDataService.document()?.config?.[key];
        return inherited === columns ? undefined : { [key]: columns };
    }

    /**
     * Adiciona imagens em um campo já existente
     * @returns
     */
    public addImageToField(fieldId: string): Observable<DocumentEntity> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) {
            return throwError(() => new Error(this._t('document.edit.service.documentNotFound')));
        }

        // Seção dona do campo capturada já: o envio demora, e ler a seção aberta no fim
        // mandaria as fotos para outra seção se o usuário trocasse no meio (ver _createItem).
        const ownerSessionId = this._editDataService.findFieldAnywhere(fieldId)?.sessionId;

        let batchUploadId: string | undefined;
        /** Evita sobrescrever o erro que `_uploadFiles` já mostrou no card. */
        let batchUploadErrorShown = false;

        // Seleciona imagens sem comprimir primeiro
        return this._mediaUtils
            .selectImagesFromGalleryWithoutCompress(this._companyController.getMaxImagesPerUpload())
            .pipe(
                // Filtra imagens
                filter(images => images.length > 0),

                // Comprime imagens (card único do lote, com progresso desde a compressão)
                switchMap(images => {
                    batchUploadId = this._generateUploadId('img-field-batch', 0);
                    this._releaseEditorLoading();
                    return this._compressImagesWithBatchProgress(images, batchUploadId, 'image');
                }),

                // Gera os items para solicitar as URLs de upload
                map(images =>
                    images.map((file, index) => ({
                        index,
                        file,
                        mimeType: file.type,
                        size: this._mediaUtils.getFileSizeInGB(file),
                        url: '',
                    }))
                ),

                // Barra o lote que não cabe no crédito — antes de gastar banda com o upload
                switchMap(items => this._assertCanAddMedia(documentId, items, 'image')),

                // Gera as URLs de upload
                switchMap(items =>
                    this._documentController
                        .generateUrlsToUploads(documentId, items)
                        .pipe(map(urls => ({ items, urls })))
                ),

                // Realiza o upload das imagens
                switchMap(({ items, urls }) =>
                    this._uploadFiles(items, urls, {
                        aggregateProgressId: batchUploadId!,
                        onAggregateUploadError: () => {
                            batchUploadErrorShown = true;
                        },
                        // Mantém apenas os arquivos que subiram com sucesso
                    }).pipe(map(() => items.filter(item => !!item.url)))
                ),

                // Cria os items no documento
                switchMap(items => {
                    const documentId = this._editDataService.getDocumentId();
                    const sessionId =
                        ownerSessionId ?? this._editDataService.getCurrentSessionId();
                    if (!documentId || !sessionId) {
                        return throwError(
                            () =>
                                new Error(
                                    this._t('document.edit.service.documentOrSessionNotFound')
                                )
                        );
                    }

                    // Converte os items para o formato de imagem
                    const images: { url: string; size: number }[] = items.map(item => ({
                        url: item.url!,
                        size: item.size,
                    }));

                    return this._documentFieldsController.addImagesToField(
                        documentId,
                        sessionId,
                        fieldId,
                        images
                    );
                }),

                // Gravado no documento: só agora o card do lote pode sumir.
                tap(() => this._finishBatchCard(batchUploadId)),

                catchError(error => {
                    this._failBatchCard(batchUploadId, batchUploadErrorShown);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Adiciona vídeos
     * @returns
     */
    public addVideo(
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        // Pasta capturada no início (antes do upload assíncrono) — ver _createItem.
        const folderId = this._editDataService.currentFolderId();

        const items: {
            index: number;
            file: File;
            mimeType: string;
            size: number;
            url?: string;
            uploadId?: string;
        }[] = [];

        const uploadIdsMap = new Map<number, string>();

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) {
            return throwError(() => new Error(this._t('document.edit.service.documentNotFound')));
        }

        return this._mediaUtils
            .selectVideosFromGalleryWithoutCompress(this._companyController.getMaxImagesPerUpload())
            .pipe(
                // Filtra vídeos
                filter(videos => videos.length > 0),

                // O arquivo sobe COMO ESTÁ, direto da galeria.
                //
                // Até aqui o navegador re-codificava cada vídeo com Canvas + MediaRecorder
                // antes de enviar, e o resultado era pior que o original em todos os
                // sentidos: a codificação era em TEMPO REAL, então o que a máquina não
                // aguentava virava quadro perdido (o arquivo que o cliente reclamou tinha
                // 20,6 fps em vez de 30); a gravação começava antes do primeiro quadro
                // desenhado, deixando 2,5 s de preto no começo; o WebM saía sem duração no
                // cabeçalho (o player marcava 0:00); e VP9/WebM é um formato que o AVPlayer
                // do iOS simplesmente NÃO ABRE — vídeo enviado pela web não tocava no app do
                // iPhone. Ainda por cima o vídeo TOCAVA em velocidade real durante a
                // compressão, então enviar 50 s de vídeo custava 50 s de aba travada.
                //
                // Quem garante o formato agora é o `VideoNormalizeWorker` no backend, com
                // ffmpeg. Ver docs/PLANO-VIDEO-SERVIDOR.md.
                map(videos =>
                    videos.map((video, index) => {
                        const uploadId = this._generateUploadId('video', index);
                        uploadIdsMap.set(index, uploadId);

                        return {
                            index,
                            file: video,
                            mimeType: video.type,
                            size: this._mediaUtils.getFileSizeInGB(video),
                            uploadId,
                            url: '',
                        };
                    })
                ),

                // Atualiza status para uploading
                tap(mappedItems => {
                    items.splice(0, items.length, ...mappedItems);
                    mappedItems.forEach(item => {
                        this._uploadProgressService.addUpload(
                            item.uploadId!,
                            item.file.name,
                            item.file.size,
                            'video'
                        );
                    });
                    // Os cards de envio já estão na tela e mostram o progresso.
                    this._releaseEditorLoading();
                }),

                // Barra o lote que não cabe no crédito — antes de gastar banda com o upload
                switchMap(mappedItems => this._assertCanAddMedia(documentId, mappedItems, 'video')),

                // Gera as URLs de upload
                switchMap(() =>
                    this._documentController
                        .generateUrlsToUploads(documentId, items)
                        .pipe(map(urls => ({ items, urls })))
                ),

                // Realiza o upload dos vídeos
                switchMap(({ items, urls }) =>
                    // Mantém apenas os arquivos que subiram com sucesso
                    this._uploadFiles(items, urls).pipe(map(() => items.filter(item => !!item.url)))
                ),

                // Cria os items no documento.
                //
                // Sem `thumbUrl` nem `duration`: os dois vêm do `VideoNormalizeWorker`, que
                // pega o quadro com ffmpeg (pulando a abertura preta da câmera, coisa que o
                // <video> do navegador não sabe fazer) e lê a duração do arquivo final. Até
                // o worker rodar, o card mostra o selo de "processando" e o vídeo já toca.
                switchMap((items): Observable<CreateFieldResponseDTO> =>
                    this._createItem(
                        items.map(item => ({
                            type: DocumentItemTypeEnum.VIDEO,
                            value: item.url,
                            size: item.size,
                        })),
                        aboveFieldId,
                        belowFieldId,
                        folderId
                    )
                ),

                // Gravados no documento: as pílulas de "salvando" podem sumir.
                tap(() =>
                    items.forEach(item => {
                        if (item.uploadId)
                            this._uploadProgressService.completeUpload(item.uploadId);
                    })
                ),

                catchError(error => {
                    // Nenhum vídeo entrou no documento. Os cards ficam com o erro à vista (como
                    // o card do lote das imagens, ver _failBatchCard): antes eles sumiam e o
                    // envio falhava calado. Os que `_uploadFiles` já marcou mantêm a mensagem.
                    const uploads = this._uploadProgressService.uploads;
                    let uploadFailed = 0;
                    uploadIdsMap.forEach(uploadId => {
                        if (uploads.find(upload => upload.id === uploadId)?.status === 'error') {
                            uploadFailed++;
                            return;
                        }
                        this._uploadProgressService.errorUpload(
                            uploadId,
                            this._t('document.edit.service.mediaProcessError')
                        );
                    });
                    // Todos falharam no envio: sem este toast o erro só existia no console.
                    if (uploadFailed > 0 && uploadFailed === uploadIdsMap.size) {
                        this._toastService.error({
                            message: this._t('document.edit.service.uploadError'),
                        });
                    }
                    return throwError(() => error);
                })
            ) as Observable<CreateFieldResponseDTO>;
    }

    /**
     * Exclui um item.
     *
     * Confirmado o dialog, o campo sai da tela na hora e o `remove-fields` real
     * só é enviado quando a janela de desfazer expira (ver `FieldRemovalService`).
     * @param itemIds IDs of items
     */
    /**
     * "Duplicar campo": a API copia o campo (clonando a mídia) e o põe logo abaixo do
     * original; o card novo chega pelo evento de campo criado, como o favorito.
     */
    public duplicateField(fieldId: string): void {
        if (this._editDataService.documentIsBlocked()) return;
        // Duplo clique no ⋯ > Duplicar fazia duas cópias: a cópia só aparece no eco.
        if (this._duplicating.has(fieldId)) return;

        const documentId = this._editDataService.getDocumentId();
        const owner = this._editDataService.findFieldAnywhere(fieldId);
        const sessionId = owner?.sessionId ?? this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        this._duplicating.add(fieldId);
        this._editDataService.beginSave();
        this._documentFieldsController.duplicateField(documentId, sessionId, fieldId).subscribe({
            complete: () => {
                this._duplicating.delete(fieldId);
                this._editDataService.endSave();
            },
            error: () => {
                this._duplicating.delete(fieldId);
                this._editDataService.endSave(false);
            },
        });
    }

    /** Campos com "Duplicar" em andamento. */
    private _duplicating = new Set<string>();

    public removeItem(itemIds: string[]): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        const sessionId = this._editDataService.getCurrentSessionId();
        if (!sessionId) return;

        this._documentFieldsController.confirmRemoveFields(itemIds.length).subscribe({
            next: dialogControl => {
                dialogControl.close();
                this._fieldRemovalService.softDelete(itemIds);
            },
        });
    }

    /**
     * O campo tem texto pré-IA guardado para restaurar?
     */
    public hasOriginalText(fieldId: string): boolean {
        if (!fieldId) return false;
        return this._originalTextService.has(this._editDataService.getDocumentId(), fieldId);
    }

    /**
     * Restaura o texto que o campo tinha antes de uma ação de IA sobrescrever,
     * pelo mesmo caminho da edição manual (update-text). O `baseText` sai do
     * próprio item (o texto que a IA gravou), então o backend não acusa edição
     * concorrente.
     */
    public restoreOriginalText(fieldId: string): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId || !fieldId) return;

        const original = this._originalTextService.get(documentId, fieldId);
        if (original === null) return;

        // O clique na ação do toast não bloqueia o botão: sem esta guarda, dois
        // cliques disparariam dois update-text.
        if (this._restoringOriginal.has(fieldId)) return;
        this._restoringOriginal.add(fieldId);

        this.updateText(
            fieldId,
            original,
            () => {
                this._restoringOriginal.delete(fieldId);
                this._toastService.error({
                    message: this._t('document.edit.originalText.restoreError'),
                });
            },
            // Sem baseText explícito: o fallback usa o valor atual do item.
            undefined,
            {
                // O backup só é descartado depois de o servidor aceitar o texto.
                keepOriginalTextBackup: true,
                onSuccess: () => {
                    this._restoringOriginal.delete(fieldId);
                    this._originalTextService.clear(documentId, fieldId);
                    this._toastService.success({
                        message: this._t('document.edit.originalText.restored'),
                    });
                },
            }
        );
    }

    /** Campos com restauração de texto original em andamento. */
    private _restoringOriginal = new Set<string>();

    /**
     * Toast de sucesso de uma ação de IA que reescreveu o texto do campo, com
     * "Desfazer" quando há texto original guardado. Os endpoints de IA não
     * emitem o toast: só aqui ele pode carregar a ação de desfazer.
     */
    public showIaSuccessToast(fieldId: string, messageKey: string): void {
        const canUndo = this.hasOriginalText(fieldId);

        const toastId = this._toastService.show({
            type: 'success',
            title: this._t('common.success'),
            message: this._t(messageKey),
            action: canUndo
                ? {
                      label: this._t('common.undo'),
                      handler: () => {
                          this._toastService.dismiss(toastId);
                          this.restoreOriginalText(fieldId);
                      },
                  }
                : undefined,
        });
    }

    /**
     * Mover itens selecionados para a seção de destino (e, opcionalmente, para uma
     * pasta de checklist dessa seção via targetFolderId).
     * @param targetSessionId ID of target section
     * @param targetFolderId ID da pasta (CHECKLIST_FOLDER) de destino; ausente = nível da seção
     */
    public moveItems(targetSessionId: string, targetFolderId?: string): Observable<DocumentEntity> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        // Verifica se o documento existe
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        // Verifica se a seção atual existe
        const sessionId = this._editDataService.getCurrentSessionId();
        if (!sessionId) return;

        // Verifica se há itens selecionados
        const itemIds = this._editDataService.selectedItems().map(item => item._id);
        if (itemIds.length === 0) return;

        // Mover depende do estado do servidor: confirma uma remoção pendente de
        // desfazer antes de enviar.
        return this._fieldRemovalService.flush().pipe(
            switchMap(() =>
                this._documentFieldsController.moveFieldsIntoSession(
                    documentId,
                    sessionId,
                    targetSessionId,
                    itemIds,
                    targetFolderId
                )
            ),
            tap(() => {
                // Aplica o move localmente com o targetFolderId que JÁ conhecemos —
                // não dependemos do echo do Pusher trazer o targetFolderId (se o
                // backend não estiver atualizado, o evento vem sem ele e os itens
                // "escapam" da pasta). É idempotente com o echo posterior.
                this._editDataService.fieldsMoved(
                    sessionId,
                    targetSessionId,
                    itemIds,
                    targetFolderId
                );
                this._editDataService.cleanSelectedItems();
            })
        );
    }

    /**
     * Mover a seleção para uma seção NOVA, criada logo depois da atual (nome: o informado,
     * ou o do título selecionado, ou o padrão). Aplica localmente com a resposta: a seção
     * nova (sem os itens movidos, que o `fieldsMoved` traz com o estado local), a ordem das
     * seções e o move. Os ecos do realtime chegam depois e não mudam nada (idempotentes).
     */
    public moveItemsIntoNewSession(
        name?: string
    ): Observable<{ document: DocumentEntity; sessionId: string }> {
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const documentId = this._editDataService.getDocumentId();
        const sessionId = this._editDataService.getCurrentSessionId();
        const itemIds = this._editDataService.selectedItems().map(item => item._id);
        if (!documentId || !sessionId || itemIds.length === 0) return EMPTY;

        return this._fieldRemovalService.flush().pipe(
            switchMap(() =>
                this._documentFieldsController.moveFieldsIntoNewSession(
                    documentId,
                    sessionId,
                    itemIds,
                    name?.trim() || undefined
                )
            ),
            tap(result => {
                const sessions = result?.document?.sessions ?? [];
                const created = sessions.find(section => section._id === result.sessionId);
                if (created) {
                    this._editDataService.sessionCreated(
                        {
                            ...created,
                            items: (created.items ?? []).filter(
                                item => !itemIds.includes(item._id)
                            ),
                        },
                        this._editDataService.currentUserId()
                    );
                    this._editDataService.sessionReordered(sessions.map(section => section._id));
                }
                this._editDataService.fieldsMoved(sessionId, result.sessionId, itemIds);
                this._editDataService.cleanSelectedItems();
            })
        );
    }

    /**
     * Atualiza o texto de um campo
     * @param fieldId ID of field
     * @param text Text to update
     */
    public updateText(
        fieldId: string,
        newText: string,
        onError?: () => void,
        baseText?: string,
        options?: {
            keepOriginalTextBackup?: boolean;
            /** Recebe a revisão que o servidor deu ao campo com este save. */
            onSuccess?: (rev?: number) => void;
            /** Revisão do campo no início da edição (compare-and-set do backend). */
            baseRev?: number | null;
        }
    ): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const request = this._getUpdateTextRequest(fieldId, newText, baseText, options?.baseRev);
        if (!request) {
            // Sem request válida (documento/seção/campo não encontrado): avisa o
            // chamador para o texto digitado não ser descartado silenciosamente
            onError?.();
            return;
        }

        // Sem o `loading` do editor: ele desabilita e apaga a folha inteira, e este é o
        // auto-save do texto (a cada pausa na digitação). A pílula "Salvando" já avisa, e o
        // editor inline serializa os saves do mesmo campo.
        this._editDataService.beginSave();
        request.subscribe({
            next: document => {
                this._editDataService.endSave();
                // Mantém o estado local em sincronia com o que foi salvo, para que
                // a próxima edição envie a base correta (senão o backend acusa
                // edição concorrente) e o preview reflita o novo texto sem realtime.
                // A revisão vem do documento devolvido pelo servidor: é ela que a
                // PRÓXIMA edição deste campo vai mandar como base.
                const rev = this._readFieldRev(document, fieldId);
                this._applyTextLocally(fieldId, newText, rev);

                // Edição manual: o texto digitado passa a ser o "original" do
                // campo, então o backup pré-IA deixa de valer. A restauração do
                // próprio backup passa `keepOriginalTextBackup` para não se
                // apagar antes de terminar.
                if (!options?.keepOriginalTextBackup) {
                    this._originalTextService.clear(this._editDataService.getDocumentId(), fieldId);
                }

                options?.onSuccess?.(rev);
            },
            error: () => {
                this._editDataService.endSave(false);
                onError?.();
            },
        });
    }

    /**
     * Tira a fonte própria dos TRECHOS (formato `font` do Delta) de todos os textos do
     * documento, em todas as seções. Chamado quando a fonte do documento muda sem nada
     * selecionado: a expectativa é "tudo na fonte nova", e um trecho que ficasse na antiga
     * parecia bug. Cada campo vai com a base (texto + revisão) do compare-and-set.
     */
    public clearSpanFonts(): void {
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        for (const session of this._editDataService.document()?.sessions ?? []) {
            for (const item of session.items ?? []) {
                const target: 'value' | 'description' =
                    item.type === DocumentItemTypeEnum.TEXT ||
                    item.type === DocumentItemTypeEnum.TITLE ||
                    item.type === DocumentItemTypeEnum.SUBTITLE
                        ? 'value'
                        : 'description';
                const current = item[target];
                if (typeof current !== 'string') continue;

                const stripped = stripDeltaAttribute(current, 'font');
                if (stripped === null) continue;

                this._editDataService.beginSave();
                this._documentFieldsController
                    .updateText(documentId, session._id, item._id, current, stripped, item.rev)
                    .subscribe({
                        next: document => {
                            this._editDataService.fieldTextUpdated(
                                item._id,
                                target,
                                stripped,
                                this._readFieldRev(document, item._id)
                            );
                            this._editDataService.endSave();
                        },
                        error: () => this._editDataService.endSave(false),
                    });
            }
        }
    }

    /**
     * Atualiza, no item em memória, o campo de texto correspondente ao tipo.
     * @param fieldId ID do campo
     * @param newText Texto salvo
     */
    private _applyTextLocally(fieldId: string, newText: string, rev?: number): void {
        const item = this._editDataService
            .currentSession()
            ?.items.find(item => item._id === fieldId);
        if (!item) return;

        if (
            item.type === DocumentItemTypeEnum.TEXT ||
            item.type === DocumentItemTypeEnum.TITLE ||
            item.type === DocumentItemTypeEnum.SUBTITLE
        ) {
            item.value = newText;
        } else {
            item.description = newText;
        }

        if (rev != null) item.rev = rev;
    }

    /** Revisão do campo no documento devolvido pelo servidor após a escrita. */
    private _readFieldRev(document: DocumentEntity, fieldId: string): number | undefined {
        for (const session of document?.sessions ?? []) {
            const field = session.items?.find(item => item._id === fieldId);
            if (field) return field.rev;
        }

        return undefined;
    }

    /**
     * Salva o override de configuração de UM item (imagem ou grupo de imagens).
     *
     * Auto-save do modal de configurações do card: aplica localmente na hora (o card e o
     * preview reagem sem esperar a rede) e persiste em seguida. Sem loading fullscreen,
     * para o usuário continuar mexendo nas opções.
     * @param fieldId ID do campo
     * @param configs Override do item (campos ausentes herdam a config do documento)
     * @param callbacks `onError` recebe a config anterior para o chamador reverter
     */
    public updateItemConfig(
        fieldId: string,
        configs: DocumentItemConfigsPartial,
        callbacks?: { onError?: (previous: DocumentItemConfigsPartial) => void }
    ): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        // A seção DONA do campo, não a que está aberta: a seta de quebra da prévia mexe
        // em títulos de qualquer seção, e pela seção atual o PATCH iria para a errada.
        const owner = this._editDataService.findFieldAnywhere(fieldId);
        const sessionId = owner?.sessionId ?? this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const previous =
            owner?.field.configs ??
            this._editDataService.currentSession()?.items?.find(item => item._id === fieldId)
                ?.configs;

        // `null` pede ao servidor para apagar a chave (o update-config mescla); no estado
        // local a chave simplesmente some, que é o mesmo para quem lê com `??`.
        this._editDataService.fieldConfigsUpdated(
            fieldId,
            Object.fromEntries(
                Object.entries(configs ?? {}).filter(([, value]) => value !== null)
            ) as DocumentItemConfigsPartial
        );
        this._editDataService.markFieldConfigSaving(fieldId);
        this._editDataService.beginSave();

        // Serializa por campo: mexer em número → data → grid em sequência disparava
        // PATCHes concorrentes e, sem compare-and-set no backend, o último a CHEGAR
        // vencia — às vezes o mais antigo. O usuário via a opção voltar sozinha.
        this._enqueueItemConfigSave(fieldId, () =>
            this._documentFieldsController
                .updateItemConfig(documentId, sessionId, fieldId, configs)
                .pipe(
                    // Toda escrita avança a revisão do campo. Guardá-la aqui evita que a
                    // próxima escrita com compare-and-set (cards da grade) seja recusada
                    // como conflito só porque o eco do realtime ainda não chegou.
                    tap(document => {
                        const rev = this._readFieldRev(document, fieldId);
                        if (rev != null) this._editDataService.fieldRevUpdated(fieldId, rev);
                    })
                )
        ).subscribe({
            // `complete` em vez de `next`: uma resposta sem corpo encerraria o
            // stream sem emitir, e o indicador ficaria preso em "Salvando…".
            complete: () => {
                this._editDataService.unmarkFieldConfigSaving(fieldId);
                this._editDataService.endSave();
            },
            error: () => {
                // Falhou: desfaz a aplicação otimista para a tela não mentir.
                this._editDataService.fieldConfigsUpdated(fieldId, previous);
                this._editDataService.unmarkFieldConfigSaving(fieldId);
                this._editDataService.endSave(false);
                callbacks?.onError?.(previous);
            },
        });
    }

    /**
     * Encadeia os saves de config de um mesmo campo, um após o outro.
     *
     * A fila é por campo: cards diferentes continuam salvando em paralelo. Um save
     * que falha não bloqueia os seguintes — a corrente segue a partir dele.
     */
    private _enqueueItemConfigSave(
        fieldId: string,
        request: () => Observable<DocumentEntity>
    ): Observable<DocumentEntity> {
        const previous = this._itemConfigSaveQueue.get(fieldId) ?? EMPTY;

        // `concat` + `ignoreElements`: o save seguinte parte quando o anterior TERMINA,
        // tenha ele emitido algo ou não. Um anterior que falha vira `EMPTY` para a
        // corrente não parar aí — senão um erro de rede deixaria o card sem salvar
        // até recarregar a página.
        const next = concat(
            previous.pipe(
                ignoreElements(),
                catchError(() => EMPTY)
            ),
            defer(request)
        ).pipe(shareReplay({ bufferSize: 1, refCount: false }));

        this._itemConfigSaveQueue.set(fieldId, next);

        // Libera a entrada quando esta for a última da fila, para o Map não crescer
        // com um campo por card configurado na sessão.
        next.subscribe({
            next: () => {
                if (this._itemConfigSaveQueue.get(fieldId) === next)
                    this._itemConfigSaveQueue.delete(fieldId);
            },
            error: () => {
                if (this._itemConfigSaveQueue.get(fieldId) === next)
                    this._itemConfigSaveQueue.delete(fieldId);
            },
        });

        return next;
    }

    /**
     * Altera a data de uma imagem única, com aplicação otimista e revert em erro.
     * @param fieldId ID do campo
     * @param date Data escolhida (meia-noite local)
     */
    public changeImageDate(fieldId: string, date: Date): void {
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        const sessionId = this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const previous = this._editDataService
            .currentSession()
            ?.items?.find(item => item._id === fieldId)?.date;

        this._editDataService.fieldDateUpdated(fieldId, date);
        this._editDataService.beginSave();

        this._documentFieldsController
            .changeImageDate(documentId, sessionId, fieldId, date)
            .subscribe({
                complete: () => this._editDataService.endSave(),
                error: () => {
                    this._editDataService.fieldDateUpdated(fieldId, previous);
                    this._editDataService.endSave(false);
                },
            });
    }

    /**
     * Altera a data de uma foto de um grupo, com aplicação otimista e revert em erro.
     * @param fieldId ID do campo
     * @param position Índice da foto no grupo
     * @param date Data escolhida (meia-noite local)
     * @param mediaId Id da mídia (value[]._id), quando existir
     */
    public changeGroupImageDate(
        fieldId: string,
        position: number,
        date: Date,
        mediaId?: string
    ): void {
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        const sessionId = this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const item = this._editDataService
            .currentSession()
            ?.items?.find(current => current._id === fieldId);
        const previous = Array.isArray(item?.value)
            ? (item.value[position] as { date?: Date } | undefined)?.date
            : undefined;

        this._editDataService.groupFieldMediaDateUpdated(fieldId, position, date);

        this._documentFieldsController
            .changeGroupImageDate(documentId, sessionId, fieldId, position, date, mediaId)
            .subscribe({
                error: () =>
                    this._editDataService.groupFieldMediaDateUpdated(fieldId, position, previous),
            });
    }

    /**
     * Persiste na API a troca de tipo de um campo de texto (TEXT/TITLE/SUBTITLE).
     * A mutação local otimista (item.type) é responsabilidade do chamador
     * (TextCardComponent) — sem loading fullscreen para não travar a UI.
     * @param fieldId ID do campo
     * @param newType Novo tipo
     * @param onError Callback chamado em falha (para reverter a mutação otimista)
     */
    public changeFieldType(
        fieldId: string,
        newType: DocumentItemTypeEnum,
        onError?: () => void
    ): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) {
            onError?.();
            return;
        }

        const sessionId = this._editDataService.getCurrentSessionId();
        if (!sessionId) {
            onError?.();
            return;
        }

        this._documentFieldsController
            .changeFieldType(documentId, sessionId, fieldId, newType)
            .subscribe({
                error: () => onError?.(),
            });
    }

    private _getUpdateTextRequest(
        fieldId: string,
        newText: string,
        baseTextOverride?: string,
        baseRevOverride?: number | null
    ): Observable<DocumentEntity> | null {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return null;

        const sessionId = this._editDataService.getCurrentSessionId();
        if (!sessionId) return null;

        const item = this._editDataService
            .currentSession()
            ?.items.find(item => item._id === fieldId);
        if (!item) return null;

        // Obtém o texto base: prioriza o texto capturado quando a edição começou
        // (detecção de conflito correta), com fallback no valor atual do item
        let baseText = baseTextOverride;
        if (baseText === undefined || baseText === null) {
            baseText = item.description;
            if (
                item.type === DocumentItemTypeEnum.TEXT ||
                item.type === DocumentItemTypeEnum.TITLE ||
                item.type === DocumentItemTypeEnum.SUBTITLE
            ) {
                baseText = item.value;
            }
        }

        // Idem para a revisão: prioriza a capturada no início da edição; sem ela
        // (ex.: campo recém-criado), a do item em memória serve de base.
        const baseRev = baseRevOverride ?? item.rev;

        return this._documentFieldsController.updateText(
            documentId,
            sessionId,
            fieldId,
            baseText,
            newText,
            baseRev ?? undefined
        );
    }

    /**
     * Gera um ID único para upload
     */
    private _generateUploadId(type: string, index: number): string {
        return `upload-${type}-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Faz o upload de um arquivo com retentativas em falhas transitórias (rede/5xx).
     * Erros definitivos (4xx, como URL assinada expirada) não são retentados.
     */
    private _uploadWithRetry(
        file: File,
        url: string,
        onProgress: (progress: number) => void
    ): Observable<string> {
        const isTransientError = (error: unknown): boolean => {
            const message = error instanceof Error ? error.message : '';
            if (message.includes('Erro de rede')) return true;

            // Extrai o status HTTP da mensagem "Falha ao enviar arquivo: <status> ..."
            const statusMatch = message.match(/Falha ao enviar arquivo: (\d{3})/);
            if (statusMatch) return Number(statusMatch[1]) >= 500;

            return false;
        };

        return this._digitalOceanStorage.uploadToSignedUrlWithProgress(file, url, onProgress).pipe(
            retry({
                count: 2,
                delay: (error, retryCount) => {
                    if (!isTransientError(error)) return throwError(() => error);
                    return timer(1000 * retryCount); // Backoff: 1s, 2s
                },
            })
        );
    }

    /**
     * Executa observables com concorrência limitada, preservando a ordem original —
     * substitui o `forkJoin` nos lotes de upload.
     *
     * Com ~100 arquivos, o `forkJoin` abre as 100 requisições de uma vez: o browser
     * enfileira além do limite por host e mantém todos os arquivos vivos em memória,
     * o que degrada a página inteira. Aqui o lote anda em janelas pequenas.
     */
    private _runWithConcurrency<T>(
        sources: Observable<T>[],
        concurrency: number = EditItemsService.UPLOAD_CONCURRENCY
    ): Observable<T[]> {
        if (sources.length === 0) return of([]);

        return from(sources.map((source, index) => ({ source, index }))).pipe(
            mergeMap(
                ({ source, index }) => source.pipe(map(value => ({ index, value }))),
                Math.max(1, concurrency)
            ),
            toArray(),
            map(parts => [...parts].sort((a, b) => a.index - b.index).map(part => part.value))
        );
    }

    /**
     * Realiza o upload dos arquivos com rastreamento de progresso.
     * Com `aggregateProgressId`, um único card recebe média ponderada (por tamanho em bytes).
     */
    private _uploadFiles(
        items: Array<{ file: File; url?: string; uploadId?: string }>,
        urls: string[],
        options?: {
            aggregateProgressId: string;
            onAggregateUploadError?: () => void;
        }
    ): Observable<string[]> {
        const aggregateProgressId = options?.aggregateProgressId;
        const onAggregateUploadError = options?.onAggregateUploadError;

        // Garante a consistência entre os arquivos e as URLs assinadas recebidas do backend
        if (urls.length !== items.length) {
            return throwError(
                () =>
                    new Error(
                        `Quantidade de URLs de upload (${urls.length}) diferente da quantidade de arquivos (${items.length})`
                    )
            );
        }

        if (aggregateProgressId) {
            const n = urls.length;
            const progresses = new Array<number>(n).fill(0);
            const weights = items.map(i => i.file.size);
            const totalWeight = weights.reduce((acc, w) => acc + w, 0) || 1;

            const updateAggregateProgress = () => {
                const weightedSum = progresses.reduce((acc, p, i) => acc + p * weights[i], 0);
                this._uploadProgressService.updateProgress(
                    aggregateProgressId,
                    Math.min(100, Math.round(weightedSum / totalWeight))
                );
            };

            return this._runWithConcurrency(
                urls.map((url, index) => {
                    const item = items[index];
                    return this._uploadWithRetry(item.file, url, progress => {
                        progresses[index] = progress;
                        updateAggregateProgress();
                    }).pipe(
                        tap(mediaUrl => {
                            item.url = mediaUrl;
                            progresses[index] = 100;
                            updateAggregateProgress();
                        }),
                        map(() => true),
                        // Falha individual não derruba o batch: os arquivos que
                        // subiram com sucesso são preservados e criados no documento
                        catchError(() => of(false))
                    );
                })
            ).pipe(
                map(results => {
                    const failed = results.filter(ok => !ok).length;

                    // Todos falharam: marca o card com erro e propaga a falha
                    if (failed === results.length) {
                        onAggregateUploadError?.();
                        this._uploadProgressService.errorUpload(
                            aggregateProgressId,
                            this._t('document.edit.service.uploadError')
                        );
                        throw new Error(this._t('document.edit.service.uploadError'));
                    }

                    // O arquivo subiu; agora falta gravá-lo no documento. A pílula
                    // continua até `_finishBatchCard`, chamado quando a mídia entra
                    // de fato no card.
                    this._uploadProgressService.startSaving(aggregateProgressId);

                    // Falha parcial: avisa quais não subiram para o usuário reenviar
                    if (failed > 0) this._notifyPartialUploadFailure(failed, results.length);

                    return items.filter(item => !!item.url).map(item => item.url!);
                })
            );
        }

        return this._runWithConcurrency(
            urls.map((url, index) => {
                const item = items[index];
                const uploadId = item.uploadId;

                if (!uploadId) {
                    throw new Error(this._t('document.edit.service.uploadIdNotFound'));
                }

                return this._uploadWithRetry(item.file, url, progress => {
                    this._uploadProgressService.updateProgress(uploadId, progress);
                }).pipe(
                    tap(mediaUrl => {
                        item.url = mediaUrl;
                        // Falta gravar no documento — a pílula continua (ver `startSaving`).
                        this._uploadProgressService.startSaving(uploadId);
                    }),
                    map(() => true),
                    // Falha individual não derruba o batch; o card próprio do
                    // arquivo já mostra o erro via errorUpload
                    catchError(error => {
                        this._uploadProgressService.errorUpload(
                            uploadId,
                            error.message || this._t('document.edit.service.uploadError')
                        );
                        return of(false);
                    })
                );
            })
        ).pipe(
            map(results => {
                const failed = results.filter(ok => !ok).length;

                // Todos falharam: propaga a falha (nenhum item será criado)
                if (failed === results.length) {
                    throw new Error(this._t('document.edit.service.uploadError'));
                }

                // Falha parcial: avisa quais não subiram para o usuário reenviar
                if (failed > 0) this._notifyPartialUploadFailure(failed, results.length);

                return items.filter(item => !!item.url).map(item => item.url!);
            })
        );
    }

    /**
     * Avisa o usuário que parte dos arquivos do batch falhou no envio
     * (os que subiram com sucesso foram mantidos e adicionados ao documento)
     */
    private _notifyPartialUploadFailure(failed: number, total: number): void {
        this._toastService.error({
            message: this._t('document.edit.service.partialUploadError', {
                failed,
                total,
            }),
        });
    }

    /**
     * Avisa o usuário que parte das imagens não pôde ser processada no cliente
     * (arquivo corrompido/formato não suportado) e por isso não será enviada
     */
    private _notifyPartialCompressFailure(skipped: number, total: number): void {
        this._toastService.error({
            message: this._t('document.edit.service.partialCompressError', {
                skipped,
                total,
            }),
        });
    }

    /**
     * Cria um item
     */
    private _createItem(
        items: DocumentItemPartial[],
        aboveFieldId?: string,
        belowFieldId?: string,
        folderId?: string | null
    ): Observable<CreateFieldResponseDTO> {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) {
            return throwError(() => new Error(this._t('document.edit.service.documentNotFound')));
        }

        // Com referência (acima/abaixo de um campo), a seção é a DONA desse campo, não a que
        // está aberta: fora disso a API não acha a referência e o campo cai no fim de outra
        // seção.
        const anchorId = aboveFieldId ?? belowFieldId;
        const sessionId =
            (anchorId ? this._editDataService.findFieldAnywhere(anchorId)?.sessionId : undefined) ??
            this._editDataService.getCurrentSessionId();
        if (!sessionId) {
            return throwError(() => new Error(this._t('document.edit.service.sessionNotFound')));
        }

        // Se a tela está dentro de uma pasta de checklist, todo item criado
        // pertence a ela. O `folderId` é capturado no INÍCIO da ação (antes de
        // uploads assíncronos) e passado pelos métodos públicos — ler o signal
        // aqui (no fim de um upload) correria risco de o contexto já ter mudado,
        // jogando a mídia pra seção. Cai no signal só como segurança.
        const fid = folderId === undefined ? this._editDataService.currentFolderId() : folderId;
        if (fid) {
            items.forEach(item => {
                if (!item.parentFolderId) item.parentFolderId = fid;
            });
        }

        // O `index` do eco `fields-created` é ABSOLUTO no array do servidor, que
        // durante a janela de desfazer ainda tem o campo removido — o card novo
        // entraria deslocado e a ordem local ficaria diferente da do servidor.
        return this._fieldRemovalService
            .flush()
            .pipe(
                switchMap(() =>
                    this._documentFieldsController.createFields(
                        documentId,
                        sessionId,
                        items,
                        aboveFieldId,
                        belowFieldId
                    )
                )
            );
    }

    /**
     * Cria uma PASTA de checklist (CHECKLIST_FOLDER) no nível da seção. Agrupa
     * campos-filhos (parentFolderId) e tem check próprio.
     */
    public addChecklistFolder(): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = DocumentItemTypeEnum.CHECKLIST_FOLDER;
        newItem.name = this._t('document.edit.checklist.folder.name');
        newItem.checklist = { done: false };

        return this._createItem([newItem]);
    }

    /**
     * Cria uma CAIXA DE DESTAQUE (CALLOUT). Nasce sem título nem texto e na cor
     * padrão (azul): o título edita no próprio card e o texto no composer. É
     * conteúdo de texto (tem tamanho e cobrança de texto na API), então pode
     * viver dentro de uma pasta de checklist como qualquer texto.
     */
    public addCallout(
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = DocumentItemTypeEnum.CALLOUT;
        newItem.configs = { calloutColor: DEFAULT_CALLOUT_COLOR };

        // Captura a pasta no início da ação (ver _createItem).
        const folderId = this._editDataService.currentFolderId();

        return this._createItem([newItem], aboveFieldId, belowFieldId, folderId);
    }

    /**
     * Cria uma GRADE DE INDICADORES (STATS). Nasce com três cards vazios, em 3 colunas,
     * estilo suave e azul; os cards editam no próprio card do editor.
     */
    public addStats(
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = DocumentItemTypeEnum.STATS;
        newItem.value = initialStatsEntries();
        newItem.configs = {
            statsColumns: DEFAULT_STATS_COLUMNS,
            statsStyle: DEFAULT_STATS_STYLE,
            calloutColor: DEFAULT_CALLOUT_COLOR,
        };

        // Captura a pasta no início da ação (ver _createItem).
        const folderId = this._editDataService.currentFolderId();

        return this._createItem([newItem], aboveFieldId, belowFieldId, folderId);
    }

    /**
     * Grava a lista de cards de uma grade de indicadores (otimista).
     *
     * Entra na MESMA fila por campo dos saves de config: cor, colunas e cards do mesmo
     * campo gravam um de cada vez. A revisão enviada é lida na hora da escrita (dentro
     * do `defer`), então saves seguidos não se acusam de conflito entre si.
     */
    public updateStats(fieldId: string, stats: DocumentStatEntry[]): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        const owner = this._editDataService.findFieldAnywhere(fieldId);
        const sessionId = owner?.sessionId ?? this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const previous = owner?.field.value;
        this._editDataService.fieldValueUpdated(fieldId, stats);
        this._editDataService.beginSave();

        this._enqueueItemConfigSave(fieldId, () => {
            const baseRev = this._editDataService.findFieldAnywhere(fieldId)?.field.rev;
            return this._documentFieldsController
                .updateStats(documentId, sessionId, fieldId, stats, baseRev ?? undefined)
                .pipe(
                    tap(document => {
                        const rev = this._readFieldRev(document, fieldId);
                        if (rev != null) this._editDataService.fieldRevUpdated(fieldId, rev);
                    })
                );
        }).subscribe({
            complete: () => this._editDataService.endSave(),
            error: () => {
                // Falhou (inclusive conflito): volta ao que estava para a tela não mentir.
                this._editDataService.fieldValueUpdated(fieldId, previous);
                this._editDataService.endSave(false);
            },
        });
    }

    /**
     * Cria uma TABELA (TABLE) com 2 colunas e 2 linhas vazias, estilo preenchido, zebra
     * e azul. As células editam no próprio card do editor. Só a web cria tabelas.
     */
    public addTable(
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = DocumentItemTypeEnum.TABLE;
        newItem.value = initialTableValue();
        newItem.configs = {
            tableStyle: DEFAULT_TABLE_STYLE,
            tableZebra: DEFAULT_TABLE_ZEBRA,
            calloutColor: DEFAULT_CALLOUT_COLOR,
        };

        // Captura a pasta no início da ação (ver _createItem).
        const folderId = this._editDataService.currentFolderId();

        return this._createItem([newItem], aboveFieldId, belowFieldId, folderId);
    }

    /**
     * Grava o conteúdo de uma tabela (otimista). Mesma fila e mesma leitura da revisão
     * na hora da escrita que `updateStats`: saves seguidos não se acusam de conflito.
     */
    public updateTable(fieldId: string, table: DocumentTableValue): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        const owner = this._editDataService.findFieldAnywhere(fieldId);
        const sessionId = owner?.sessionId ?? this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const previous = owner?.field.value;
        this._editDataService.fieldValueUpdated(fieldId, table);
        this._editDataService.beginSave();

        this._enqueueItemConfigSave(fieldId, () => {
            const baseRev = this._editDataService.findFieldAnywhere(fieldId)?.field.rev;
            return this._documentFieldsController
                .updateTable(documentId, sessionId, fieldId, table, baseRev ?? undefined)
                .pipe(
                    tap(document => {
                        const rev = this._readFieldRev(document, fieldId);
                        if (rev != null) this._editDataService.fieldRevUpdated(fieldId, rev);
                    })
                );
        }).subscribe({
            complete: () => this._editDataService.endSave(),
            error: () => {
                // Falhou (inclusive conflito): volta ao que estava para a tela não mentir.
                this._editDataService.fieldValueUpdated(fieldId, previous);
                this._editDataService.endSave(false);
            },
        });
    }

    /**
     * Cria um GRÁFICO (CHART) já preenchido: tipo, dados e aparência vêm do assistente. O
     * SVG não vai junto (a API sempre desenha de novo e grava); o card aparece com o eco
     * do servidor, já desenhado.
     */
    public addChart(
        chart: ChartValue,
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = DocumentItemTypeEnum.CHART;
        newItem.value = withoutSvg(chart);

        // Captura a pasta no início da ação (ver _createItem).
        const folderId = this._editDataService.currentFolderId();

        return this._createItem([newItem], aboveFieldId, belowFieldId, folderId);
    }

    /**
     * Grava o gráfico inteiro (otimista): a tela já mostra o SVG que a web desenhou, igual
     * ao que a API vai desenhar, e o eco do servidor confirma. Mesma fila por campo e
     * mesma leitura da revisão na hora da escrita que `updateTable`.
     */
    public updateChart(fieldId: string, chart: ChartValue): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        const owner = this._editDataService.findFieldAnywhere(fieldId);
        const sessionId = owner?.sessionId ?? this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const previous = owner?.field.value;
        this._editDataService.fieldValueUpdated(fieldId, chart);
        this._editDataService.beginSave();

        this._enqueueItemConfigSave(fieldId, () => {
            const baseRev = this._editDataService.findFieldAnywhere(fieldId)?.field.rev;
            return this._documentFieldsController
                .updateChart(documentId, sessionId, fieldId, withoutSvg(chart), baseRev ?? undefined)
                .pipe(
                    tap(document => {
                        const rev = this._readFieldRev(document, fieldId);
                        if (rev != null) this._editDataService.fieldRevUpdated(fieldId, rev);
                    })
                );
        }).subscribe({
            complete: () => this._editDataService.endSave(),
            error: () => {
                // Falhou (inclusive conflito): volta ao que estava para a tela não mentir.
                this._editDataService.fieldValueUpdated(fieldId, previous);
                this._editDataService.endSave(false);
            },
        });
    }

    /**
     * Cria um SEMÁFORO (SEMAPHORE) com três itens vazios, a escala nas palavras padrão do
     * idioma e formato lista. Os itens editam no próprio card.
     */
    public addSemaphore(
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = DocumentItemTypeEnum.SEMAPHORE;
        newItem.value = initialSemaphoreValue({
            ok: this._t(SEMAPHORE_SCALE_LABEL_KEYS.ok),
            warn: this._t(SEMAPHORE_SCALE_LABEL_KEYS.warn),
            bad: this._t(SEMAPHORE_SCALE_LABEL_KEYS.bad),
        });
        // Sem config: só existe o formato lista (o de tabela foi retirado).

        // Captura a pasta no início da ação (ver _createItem).
        const folderId = this._editDataService.currentFolderId();

        return this._createItem([newItem], aboveFieldId, belowFieldId, folderId);
    }

    /**
     * Grava o conteúdo de um semáforo (otimista). Mesma fila e mesma leitura da revisão
     * na hora da escrita que `updateStats`: saves seguidos não se acusam de conflito.
     */
    public updateSemaphore(fieldId: string, semaphore: DocumentSemaphoreValue): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        const owner = this._editDataService.findFieldAnywhere(fieldId);
        const sessionId = owner?.sessionId ?? this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const previous = owner?.field.value;
        this._editDataService.fieldValueUpdated(fieldId, semaphore);
        this._editDataService.beginSave();

        this._enqueueItemConfigSave(fieldId, () => {
            const baseRev = this._editDataService.findFieldAnywhere(fieldId)?.field.rev;
            return this._documentFieldsController
                .updateSemaphore(documentId, sessionId, fieldId, semaphore, baseRev ?? undefined)
                .pipe(
                    tap(document => {
                        const rev = this._readFieldRev(document, fieldId);
                        if (rev != null) this._editDataService.fieldRevUpdated(fieldId, rev);
                    })
                );
        }).subscribe({
            complete: () => this._editDataService.endSave(),
            error: () => {
                // Falhou (inclusive conflito): volta ao que estava para a tela não mentir.
                this._editDataService.fieldValueUpdated(fieldId, previous);
                this._editDataService.endSave(false);
            },
        });
    }

    /**
     * Cria uma QUEBRA DE PÁGINA (PAGE_BREAK). Item sem conteúdo: nasce sem
     * `value` e sem `size` — não é cobrado em tokens e não imprime nada. O que
     * vier depois dele começa numa página nova do PDF.
     */
    public addPageBreak(
        aboveFieldId?: string,
        belowFieldId?: string
    ): Observable<CreateFieldResponseDTO> {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return EMPTY;

        const newItem = new DocumentItemPartial();
        newItem.type = DocumentItemTypeEnum.PAGE_BREAK;

        // `null` explícito: a quebra fica SEMPRE no nível da seção, nunca dentro
        // de uma pasta de checklist — quebrar página é estrutura da seção, não
        // tarefa da pasta. Sem isso, o fallback do _createItem herdaria a pasta
        // aberta se a opção passasse a aparecer lá dentro.
        return this._createItem([newItem], aboveFieldId, belowFieldId, null);
    }
}

/** O gráfico como vai para a API: sem os SVGs, que ela mesma desenha e grava. */
function withoutSvg(chart: ChartValue): ChartValue {
    const { svg: _svg, svgCompact: _svgCompact, ...rest } = chart;
    return rest;
}
